import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { IntegrationAxios } from "../../../axios/integration/integration.axios";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { Modal } from "../../../components/modal/Modal";
import { Integration } from "../../../model/integration/Integration";
import { integrationSliceSelectors } from "../../../redux/integration/integration.selector";
import { integrationActions } from "../../../redux/integration/integration.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { AtiraToast } from "../../../utils/AtiraToast";
import { IntegrationConfigWhatsappMeta } from "../../../model/integration/types/config/IntegrationConfigWhatsappMeta";
import { loadFacebookSdk } from "../utils/integration-utils";
import { WhatsappMetaEmbeddedSignupFinalizeDto } from "../../../model/integration/dto/WhatsappMetaEmbeddedSignupFinalizeDto";
import { WhatsappMetaIntegrationDto } from "../../../model/integration/dto/WhatsappMetaIntegrationDto";
import { faFacebook } from "@fortawesome/free-brands-svg-icons/faFacebook";
import { Info } from "../../../components/Info";
import { Input } from "../../../components/Input";
import { Checkbox } from "../../../utils/local-antd";

type Props = {
  open: boolean;
  onClose: () => void;
};

type FormShape = {
  data: WhatsappMetaIntegrationDto;
};

type EmbeddedSignupState = {
  code?: string;
  phoneNumberId?: string;
  wabaId?: string;
  businessId?: string;
  pagesIds?: string[];
  finalized?: boolean;
  pin?: string;
  isBizApp?: boolean;
};

export const IntegrationAddModalWhatsappMeta: React.FC<Props> = ({
  open,
  onClose,
}) => {
  const [embeddedLoading, setEmbeddedLoading] = useState(false);
  const [pinCode, setPinCode] = useState("");
  const [hasPin, setHasPin] = useState(false);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const embeddedSignupStateRef = useRef<EmbeddedSignupState>({});
  const embeddedSignupMessageHandlerRef = useRef<
    ((event: MessageEvent) => void | Promise<void>) | null
  >(null);

  const embeddedSignupListenerRef = useRef<
    ((event: MessageEvent) => void) | null
  >(null);
  const existingIntegration = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaIntegration,
  ) as Integration<IntegrationConfigWhatsappMeta> | null;

  const defaultValues = useMemo<FormShape>(
    () => ({
      data: {
        phoneNumber: existingIntegration?.config.phoneNumber || "",
        phoneNumberId: existingIntegration?.config.phoneNumberId || "",
        wabaId: existingIntegration?.config.wabaId || "",
        accessToken: "",
        welcomeMessage:
          existingIntegration?.config.conversationSettings.welcomeMessage || "",
        historyRetentionDays:
          existingIntegration?.config.conversationSettings
            .historyRetentionDays || 7,
        pin: existingIntegration?.config?.pinCode || "",
      },
    }),
    [existingIntegration],
  );

  const onConnectWithMeta = async () => {
    try {
      setEmbeddedLoading(true);
      const configId = process.env.REACT_APP_WHATSAPP_META_CONFIG_ID;

      if (!configId) {
        AtiraToast.error(
          t("integration.add-modal.whatsapp.meta.modal.embedded.missingConfig"),
        );
        return;
      }

      embeddedSignupStateRef.current = {};
      cleanupEmbeddedSignupListener();

      const listener = (event: MessageEvent) => {
        embeddedSignupMessageHandlerRef.current?.(event);
      };

      embeddedSignupListenerRef.current = listener;
      window.addEventListener("message", listener);

      const facebookSdk = await loadFacebookSdk();

      facebookSdk.login(
        // Do not remove this function wrapper
        function (res) {
          onMetaResponse(res);
        },
        {
          config_id: configId,
          response_type: "code",
          override_default_response_type: true,
          extras: {
            // feature: "whatsapp_embedded_signup",
            sessionInfoVersion: 3,
            setup: {},
            featureType: "whatsapp_business_app_onboarding",
          },
        },
      );
    } catch (e: any) {
      cleanupEmbeddedSignupListener();
      console.log(e);
      AtiraToast.error(
        t("integration.add-modal.whatsapp.meta.modal.embedded.failed"),
      );
    }
  };

  const cleanupEmbeddedSignupListener = () => {
    const listener = embeddedSignupListenerRef.current;

    if (listener) {
      window.removeEventListener("message", listener);
      embeddedSignupListenerRef.current = null;
    }
  };

  const stopEmbeddedSignup = (message?: string) => {
    cleanupEmbeddedSignupListener();
    setEmbeddedLoading(false);

    if (message) {
      AtiraToast.error(message);
    }
  };

  const onMetaResponse = async (response: any) => {
    const code = response?.authResponse?.code;

    if (!code) {
      cleanupEmbeddedSignupListener();
      setEmbeddedLoading(false);
      return;
    }

    embeddedSignupStateRef.current = {
      ...embeddedSignupStateRef.current,
      code,
    };

    await maybeFinalizeEmbeddedSignup();
  };

  const finalizeEmbeddedSignup = async () => {
    const state = embeddedSignupStateRef.current;

    await IntegrationAxios.finalizeWhatsappMetaEmbeddedSignup({
      userId,
      code: state.code!,
      phoneNumberId: state.phoneNumberId!,
      wabaId: state.wabaId!,
      businessId: state.businessId!,
      pagesIds: state.pagesIds!,
      pinCode: pinCode,
      isBizApp: state.isBizApp,
    } as WhatsappMetaEmbeddedSignupFinalizeDto);

    await dispatch(integrationActions.getUserIntegrations({ userId })).unwrap();

    if (state.isBizApp) {
      AtiraToast.success(
        t("integration.add-modal.whatsapp.meta.modal.embedded.bizAppSyncing"),
        { duration: 15000 },
      );
    } else {
      AtiraToast.success(t("integration.add-modal.success-update"));
    }

    cleanupEmbeddedSignupListener();
    onClose();
    setEmbeddedLoading(false);
  };

  const handleEmbeddedSignupMessage = async (event: MessageEvent) => {
    let hostname = "";

    try {
      hostname = new URL(event.origin).hostname;
    } catch {
      return;
    }

    if (!/facebook\.com$/i.test(hostname) || typeof event.data !== "string") {
      return;
    }

    let parsedData: any = event.data;

    if (typeof parsedData === "string") {
      try {
        parsedData = JSON.parse(parsedData);
      } catch {
        return;
      }
    }

    if (parsedData?.type !== "WA_EMBEDDED_SIGNUP") {
      return;
    }

    // A number kept on the WhatsApp Business app finishes with its own event,
    // carrying the same ids
    const isBizApp =
      parsedData?.event === "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING";

    if (parsedData?.event === "FINISH" || isBizApp) {
      // Untested against Meta yet: without the ids it would otherwise spin
      // forever waiting for them
      if (
        isBizApp &&
        (!parsedData.data?.phone_number_id || !parsedData.data?.waba_id)
      ) {
        stopEmbeddedSignup(
          t("integration.add-modal.whatsapp.meta.modal.embedded.unsupported"),
        );
        return;
      }

      embeddedSignupStateRef.current = {
        ...embeddedSignupStateRef.current,
        phoneNumberId: parsedData.data.phone_number_id,
        wabaId: parsedData.data.waba_id,
        businessId: parsedData.data.business_id,
        pagesIds: parsedData.data.page_ids,
        // A plain FINISH arriving after it must not undo it
        isBizApp: embeddedSignupStateRef.current.isBizApp || isBizApp,
      };

      await maybeFinalizeEmbeddedSignup();

      return;
    }

    if (parsedData?.event === "FINISH_ONLY_WABA") {
      stopEmbeddedSignup(
        t("integration.add-modal.whatsapp.meta.modal.embedded.noPhone"),
      );
      return;
    }

    if (String(parsedData?.event || "").startsWith("FINISH_")) {
      stopEmbeddedSignup(
        t("integration.add-modal.whatsapp.meta.modal.embedded.unsupported"),
      );
      return;
    }

    // Closing the window is a cancel with no data; reporting a problem from
    // inside the flow is a cancel that carries the error
    if (parsedData?.event === "CANCEL") {
      stopEmbeddedSignup(parsedData?.data?.error_message);
      return;
    }

    if (parsedData?.event === "ERROR") {
      stopEmbeddedSignup(
        parsedData?.data?.error_message || t("common.wrong_contact_support"),
      );
    }
  };

  const maybeFinalizeEmbeddedSignup = async () => {
    const state = embeddedSignupStateRef.current;

    if (
      !state.code ||
      !state.wabaId ||
      !state.phoneNumberId ||
      state.finalized
    ) {
      return;
    }

    state.finalized = true;

    try {
      await finalizeEmbeddedSignup();
    } catch (e) {
      state.finalized = false;
      setEmbeddedLoading(false);
      AtiraToast.apiError(e, { duration: 10000 });
      cleanupEmbeddedSignupListener();
    }
  };

  useEffect(() => {
    if (open) {
      embeddedSignupStateRef.current = {};
      setPinCode("");
    }
  }, [defaultValues, open]);

  useEffect(() => {
    embeddedSignupMessageHandlerRef.current = handleEmbeddedSignupMessage;
  }, [handleEmbeddedSignupMessage]);

  useEffect(() => {
    return () => {
      cleanupEmbeddedSignupListener();
    };
  }, []);

  return (
    <Modal
      open={open}
      onClose={() => {
        onClose();
      }}
      title={t("integration.add-modal.whatsapp.meta.modal.title")}
      width="42rem"
    >
      <Flex flexDirection="column" gap="m">
        <Flex
          flexDirection="column"
          gap="s"
          padding="m"
          backgroundColor="lightMain"
          borderRadius="md"
        >
          <Flex flexDirection="column" gap="l">
            <Flex flexDirection="column" gap="s">
              <Info
                info={t("integration.whatsapp.meta.connect.modal.pin.info")}
              />
              <Info
                color="warning"
                info={t("integration.whatsapp.meta.connect.modal.pin.info-2")}
              />
            </Flex>

            <Flex flexDirection="column" gap="s">
              <Checkbox
                value={hasPin}
                onChange={(e) => setHasPin(e.target.checked)}
              >
                <Text>
                  {t(
                    "integration.whatsapp.meta.connect.modal.pin.checkbox.title",
                  )}
                </Text>
              </Checkbox>
              <Input
                required
                onChange={(e) => setPinCode(e.target.value)}
                placeholder={t(
                  "integration.whatsapp.meta.connect.modal.pin.input.placeholder",
                )}
                maxLength={6}
                disabled={!hasPin}
                value={pinCode}
              />
            </Flex>

            <Button
              onClick={onConnectWithMeta}
              loading={embeddedLoading}
              icon={faFacebook}
              disabled={
                embeddedLoading ||
                (hasPin ? !pinCode || pinCode.length < 6 : false)
              }
            >
              <Text color="white">
                {t(
                  "integration.add-modal.whatsapp.meta.modal.embedded.connect",
                )}
              </Text>
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Modal>
  );
};
